import React from 'react';
import { useTranslation } from 'react-i18next';
import { HrLeaveRequestUiModel, HrWfhRequestUiModel } from '../../shared/ui-models/hr.ui-model';
import { CHIP, formatDateTime, formatRange, STATUS_CHIP, TABLE_WRAP, TD, TH } from './hr.util';

const StatusChip: React.FC<{ status: HrLeaveRequestUiModel['status'] }> = ({ status }) => {
  const { t } = useTranslation();
  return (
    <span className={`${CHIP} ${STATUS_CHIP[status] || STATUS_CHIP.cancelled}`}>
      {t(`HR_MODULE.STATUS.${status}`, status)}
    </span>
  );
};

const Description: React.FC<{ text: string; rejectReason?: string }> = ({ text, rejectReason }) => {
  const { t } = useTranslation();
  return (
    <div className="flex max-w-[280px] flex-col gap-[2px]">
      <span className="truncate text-neutral-300" title={text}>
        {text || '—'}
      </span>
      {rejectReason ? (
        <span className="text-[12px] text-scarlet">
          {t('HR_MODULE.REJECT_REASON', { reason: rejectReason })}
        </span>
      ) : null}
    </div>
  );
};

export const LeaveRequestsTable: React.FC<{
  rows: HrLeaveRequestUiModel[];
  showEmployee?: boolean;
  renderActions?: (row: HrLeaveRequestUiModel) => React.ReactNode;
}> = ({ rows, showEmployee, renderActions }) => {
  const { t } = useTranslation();
  const breakdown = (row: HrLeaveRequestUiModel) => {
    const parts = [row.startBreakdown, row.endBreakdown].filter((b) => b !== 'full_day');
    if (parts.length === 0) return null;
    const label =
      row.startDate === row.endDate || row.startBreakdown === row.endBreakdown
        ? t(`HR_MODULE.BREAKDOWN.${parts[0]}`)
        : `${t(`HR_MODULE.BREAKDOWN.${row.startBreakdown}`)} → ${t(
            `HR_MODULE.BREAKDOWN.${row.endBreakdown}`
          )}`;
    return <span className="block text-[12px] text-neutral-300">{label}</span>;
  };

  return (
    <div className={TABLE_WRAP}>
      <table className="w-full min-w-[760px] border-collapse">
        <thead className="bg-creams-100">
          <tr>
            {showEmployee ? <th className={TH}>{t('HR_MODULE.COLUMNS.EMPLOYEE')}</th> : null}
            <th className={TH}>{t('HR_MODULE.COLUMNS.TYPE')}</th>
            <th className={TH}>{t('HR_MODULE.COLUMNS.DATES')}</th>
            <th className={`${TH} text-right`}>{t('HR_MODULE.COLUMNS.DAYS')}</th>
            <th className={TH}>{t('HR_MODULE.COLUMNS.DESCRIPTION')}</th>
            <th className={TH}>{t('HR_MODULE.COLUMNS.SUBMITTED')}</th>
            <th className={TH}>{t('HR_MODULE.COLUMNS.STATUS')}</th>
            {renderActions ? <th className={TH} /> : null}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {rows.map((row) => (
            <tr key={row.id} className="bg-neutral-0">
              {showEmployee ? (
                <td className={`${TD} whitespace-nowrap font-gilroy-medium`}>
                  {row.employee?.name || '—'}
                </td>
              ) : null}
              <td className={`${TD} whitespace-nowrap`}>
                <span className="inline-flex items-center gap-[8px]">
                  <span
                    className="inline-block size-[10px] shrink-0 rounded-full"
                    style={{ backgroundColor: row.leaveTypeColor }}
                  />
                  {row.leaveTypeName}
                </span>
              </td>
              <td className={`${TD} whitespace-nowrap`}>
                {formatRange(row.startDate, row.endDate)}
                {breakdown(row)}
              </td>
              <td className={`${TD} whitespace-nowrap text-right`}>
                {row.days === undefined ? '—' : row.days}
              </td>
              <td className={TD}>
                <Description text={row.description} rejectReason={row.rejectReason} />
              </td>
              <td className={`${TD} whitespace-nowrap text-neutral-300`}>
                {formatDateTime(row.createdAt)}
              </td>
              <td className={TD}>
                <StatusChip status={row.status} />
              </td>
              {renderActions ? (
                <td className={`${TD} whitespace-nowrap text-right`}>{renderActions(row)}</td>
              ) : null}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export const WfhRequestsTable: React.FC<{
  rows: HrWfhRequestUiModel[];
  showEmployee?: boolean;
  renderActions?: (row: HrWfhRequestUiModel) => React.ReactNode;
}> = ({ rows, showEmployee, renderActions }) => {
  const { t } = useTranslation();
  return (
    <div className={TABLE_WRAP}>
      <table className="w-full min-w-[640px] border-collapse">
        <thead className="bg-creams-100">
          <tr>
            {showEmployee ? <th className={TH}>{t('HR_MODULE.COLUMNS.EMPLOYEE')}</th> : null}
            <th className={TH}>{t('HR_MODULE.COLUMNS.DATES')}</th>
            <th className={TH}>{t('HR_MODULE.COLUMNS.DESCRIPTION')}</th>
            <th className={TH}>{t('HR_MODULE.COLUMNS.SUBMITTED')}</th>
            <th className={TH}>{t('HR_MODULE.COLUMNS.STATUS')}</th>
            {renderActions ? <th className={TH} /> : null}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {rows.map((row) => (
            <tr key={row.id} className="bg-neutral-0">
              {showEmployee ? (
                <td className={`${TD} whitespace-nowrap font-gilroy-medium`}>
                  {row.employee?.name || '—'}
                </td>
              ) : null}
              <td className={`${TD} whitespace-nowrap`}>
                {formatRange(row.startDate, row.endDate)}
              </td>
              <td className={TD}>
                <Description text={row.description} rejectReason={row.rejectReason} />
              </td>
              <td className={`${TD} whitespace-nowrap text-neutral-300`}>
                {formatDateTime(row.createdAt)}
              </td>
              <td className={TD}>
                <StatusChip status={row.status} />
              </td>
              {renderActions ? (
                <td className={`${TD} whitespace-nowrap text-right`}>{renderActions(row)}</td>
              ) : null}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
